import { graphql } from "generated/gql";

export const CoreMaterialImageFieldsFragment = graphql(/* GraphQL */ `
  fragment MaterialImageFields on MaterialImage {
    id
    fileName
    bucketName
    description
    capturedAt
    capturedBy
    uploadedAt
    uploadedBy {
      id
      avatarUrl
      givenName
      familyName
      initials
    }
    manualImport
    isPrimaryImage
  }
`);

export const CoreMaterialHistoryFieldsFragment = graphql(/* GraphQL */ `
  fragment MaterialHistoryFields on PlantMaterialHistory {
    createdAt
    wasManualImport
    createdBy {
      id
      initials
      email
      givenName
      familyName
      avatarUrl
      isActivated
      accessRole
      deactivatedAt
    }
    historicCreatedByInitials
    wasArchived
    wasRestored
    fromStatus
    toStatus
    fromMaterialGroup
    toMaterialGroup
    fromCollectionSiteLocationId
    fromCollectionSiteLocation {
      code
      name
      archived
    }
    toCollectionSiteLocationId
    toCollectionSiteLocation {
      code
      name
      archived
    }
    fromQuantity
    toQuantity
    fromMassPlanting
    toMassPlanting
    fromWeight
    toWeight
    fromFirstPresent
    toFirstPresent
    fromFirstAbsent
    toFirstAbsent
    fromPosition {
      longitude
      latitude
      elevation
    }
    toPosition {
      longitude
      latitude
      elevation
    }
    fromSex
    toSex
    fromTagNumber
    toTagNumber
    fromNotes
    toNotes
    madePublic
    madePrivate
  }
`);

export const CoreMaterialFieldsFragment = graphql(/* GraphQL */ `
  fragment MaterialFields on PlantMaterial {
    id
    archived
    qualifier
    quantity
    massPlanting
    weight
    status
    firstPresent
    firstAbsent
    materialGroup
    notes
    notesData
    sex
    tagNumber
    daysOverdueFollowUp
    public
    collectionSiteLocation {
      ...CollectionSiteLocationFields
    }
    tagsConnection {
      nodes {
        ...RecordTagFields
      }
    }
    lastObserved
    daysOverdueFollowUp
    followUp {
      count
      period
    }
    lastObservation {
      ...ObservationFields
    }
    condition
    createdBy {
      givenName
      familyName
      id
      avatarUrl
      initials
    }
    primaryImage {
      ...MaterialImageFields
    }
    images {
      ...MaterialImageFields
    }
    position {
      latitude
      longitude
      elevation
    }
    accession {
      id
      accessionNumber
      donor
      accessionDate
      scientificName
      archived
      collectionSite {
        urlSlug
      }
      tagsConnection {
        nodes {
          ...RecordTagFields
        }
      }
      taxon {
        id
        scientificName
        commonName
        nativeDistribution
        fullyValidated
        archived
        createdAt
        updatedAt
        tagsConnection {
          nodes {
            ...RecordTagFields
          }
        }
        sharedScientificName {
          ...ScientificNameFields
        }
      }
      provenanceCategoryCode
      provenanceCategory {
        codeId
        name
        ordering
      }
      plantMaterial(filters: $relatedMaterialsFilters) {
        id
        archived
        status
        qualifier
        materialGroup
        massPlanting
        public
        primaryImage {
          ...MaterialImageFields
        }
        images {
          ...MaterialImageFields
        }
        collectionSiteLocation {
          id
          name
          code
          archived
        }
        accession {
          scientificName
        }
      }
    }
    historyConnection {
      nodes {
        ...MaterialHistoryFields
      }
    }
  }
`);

export const getMaterialByIdDocument = graphql(/* GraphQL */ `
  query GetMaterialById(
    $id: ID!
    $relatedMaterialsFilters: PlantMaterialFilter
  ) {
    material(id: $id) {
      ...MaterialFields
    }
  }
`);

export const getObservationDocument = graphql(/* GraphQL */ `
  query GetObservation($id: ID!) {
    observation(id: $id) {
      ...ObservationFields
    }
  }
`);

export const getMaterialDocument = graphql(/* GraphQL */ `
  query GetMaterial(
    $organisationSubdomain: String!
    $collectionSiteSlug: String!
    $accessionNumber: String!
    $qualifier: String!
    $relatedMaterialsFilters: PlantMaterialFilter
  ) {
    org: organisationBySubdomain(subdomain: $organisationSubdomain) {
      site: collectionSiteBySlug(slug: $collectionSiteSlug) {
        result: plantMaterial(
          accessionNumber: $accessionNumber
          qualifier: $qualifier
        ) {
          ...MaterialFields
        }
      }
    }
  }
`);

export const updatePlantMaterialDocument = graphql(/* GraphQL */ `
  mutation UpdatePlantMaterial(
    $materialId: ID!
    $updates: PlantMaterialUpdates!
    $relatedMaterialsFilters: PlantMaterialFilter
  ) {
    updatePlantMaterial(materialId: $materialId, updates: $updates) {
      __typename
      ... on PlantMaterial {
        ...MaterialFields
      }
      ... on GenericError {
        message
      }
    }
  }
`);

export const updateMaterialImageDocument = graphql(/* GraphQL */ `
  mutation UpdateMaterialImage(
    $materialImageId: ID!
    $updates: MaterialImageUpdates!
  ) {
    updateMaterialImage(materialImageId: $materialImageId, updates: $updates) {
      __typename
      ... on MaterialImage {
        ...MaterialImageFields
      }
      ... on GenericError {
        message
      }
    }
  }
`);

export const archiveMaterialDocument = graphql(/* GraphQL */ `
  mutation ArchiveMaterial(
    $materialId: ID!
    $relatedMaterialsFilters: PlantMaterialFilter
  ) {
    archivePlantMaterial(materialId: $materialId) {
      __typename
      ... on PlantMaterial {
        ...MaterialFields
      }
      ... on GenericError {
        message
      }
    }
  }
`);

export const archiveMaterialsDocument = graphql(/* GraphQL */ `
  mutation ArchiveMaterials($ids: [ID!]!) {
    archivePlantMaterials(ids: $ids) {
      plantMaterials {
        id
        archived
      }
      errors {
        ... on GenericError {
          message
        }
      }
      success
    }
  }
`);

export const restoreMaterialDocument = graphql(/* GraphQL */ `
  mutation RestoreMaterial(
    $materialId: ID!
    $relatedMaterialsFilters: PlantMaterialFilter
  ) {
    restorePlantMaterial(materialId: $materialId) {
      __typename
      ... on PlantMaterial {
        ...MaterialFields
      }
      ... on GenericError {
        message
      }
    }
  }
`);

export const setPrimaryImageDocument = graphql(/* GraphQL */ `
  mutation SetPrimaryImage($materialImageId: ID!) {
    setPrimaryImage(materialImageId: $materialImageId) {
      __typename
      ... on MaterialImage {
        ...MaterialImageFields
      }
      ... on GenericError {
        message
      }
    }
  }
`);
export const unsetPrimaryImageDocument = graphql(/* GraphQL */ `
  mutation UnsetPrimaryImage($materialImageId: ID!) {
    unsetPrimaryImage(materialImageId: $materialImageId) {
      __typename
      ... on MaterialImage {
        ...MaterialImageFields
      }
      ... on GenericError {
        message
      }
    }
  }
`);

export const uploadMaterialImageDocument = graphql(/* GraphQL */ `
  mutation UploadMaterialImage(
    $plantMaterialId: ID!
    $image: MaterialImageUpload!
  ) {
    uploadMaterialImage(plantMaterialId: $plantMaterialId, image: $image) {
      ... on MaterialImage {
        __typename
        ...MaterialImageFields
      }
    }
  }
`);

export const archiveMaterialImageDocument = graphql(/* GraphQL */ `
  mutation ArchiveMaterialImage($materialImageId: ID!) {
    archiveMaterialImage(materialImageId: $materialImageId) {
      __typename
      ... on MaterialImage {
        ...MaterialImageFields
      }
      ... on GenericError {
        message
      }
    }
  }
`);
export const addTagToMaterialsDocument = graphql(/* GraphQL */ `
  mutation AddTagToMaterials($tagId: ID!, $ids: [ID!]!) {
    addTagPlantMaterials(tagId: $tagId, ids: $ids) {
      success
      plantMaterials {
        id
      }
      errors {
        ... on GenericError {
          message
        }
      }
    }
  }
`);
export const removeTagFromMaterialsDocument = graphql(/* GraphQL */ `
  mutation RemoveTagFromMaterials($tagId: ID!, $ids: [ID!]!) {
    removeTagPlantMaterials(tagId: $tagId, ids: $ids) {
      success
      plantMaterials {
        id
      }
      errors {
        ... on GenericError {
          message
        }
      }
    }
  }
`);
